import { autoUpdate, flip, offset, shift, useFloating, UseFloatingOptions } from "@floating-ui/react";
import FocusTrap from "focus-trap-react";
import { ActionIcon } from "PFComponents/action_icon";
import { Button, ButtonProps } from "PFComponents/button";
import Calendar from "PFComponents/calendar/calendar";
import { DoubleSectionPill } from "PFComponents/double_section_pill/double_section_pill";
import { PillProps } from "PFComponents/pill";
import { Tooltip } from "PFComponents/tooltip";
import { utc } from "PFCore/helpers/date";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { EventKey } from "PFTypes/event_key";
import { RefObject, SyntheticEvent, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";

import { CustomValueOption, OptionOriginal } from "../../custom_value_select";
import css from "./calendar_badge.module.scss";

export type CalendarBadgeProps = {
  displayValue: string;
  onRemove: (value: CustomValueOption, event: SyntheticEvent) => void;
  value: CustomValueOption;
  onDateChange: (item: CustomValueOption, event: SyntheticEvent) => void;
  isLocked: boolean;
  floatingOptions?: Partial<UseFloatingOptions>;
  tabIndex?: number;
} & Pick<PillProps, "small">;

// TODO: [SP-2875] Got rid of a custom calendar_badge component
export const CalendarBadge = ({
  displayValue,
  onRemove,
  value,
  onDateChange,
  isLocked,
  floatingOptions,
  small,
  tabIndex
}: CalendarBadgeProps) => {
  const { t } = useTranslation("core");
  const [showCalendar, setShowCalendar] = useState(false);
  const [date, setDate] = useState<string | null>(value.original?.expiryDate as string);

  const badgeRef = useRef<HTMLDivElement>(null);
  const modalSelector = document.getElementById("modal_region");

  const { formatISODate, formatDate } = useDateFormatter();

  const { refs, floatingStyles } = useFloating({
    strategy: "fixed",
    placement: "top",
    middleware: [
      offset(15),
      flip(),
      shift({
        mainAxis: true,
        crossAxis: true
      })
    ],
    whileElementsMounted: autoUpdate,
    ...floatingOptions
  });

  const handleShowCalendar = () => {
    setShowCalendar(true);
  };

  const handleHideCalendar = () => {
    setShowCalendar(false);
  };

  const handleCalendarKeyDown = (event) => {
    if (event.key === EventKey.Escape) {
      handleHideCalendar();
    }
  };

  useEffect(() => {
    const handleClick = (event) => {
      if (modalSelector && !modalSelector.contains(event.target)) {
        handleHideCalendar();
      }
    };

    document.addEventListener("click", handleClick, true);

    return () => {
      document.removeEventListener("click", handleClick);
    };
  }, []);

  const CalendarRenderer = (
    <FocusTrap focusTrapOptions={{ preventScroll: true }}>
      {/* eslint-disable-next-line max-len */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
      <div
        role="dialog"
        className={css.calendarWrapper}
        ref={refs.setFloating}
        style={floatingStyles}
        // Fix calendar clicks trigger original select click, causing calendar nested dropdowns not to open
        onClick={(event) => event.stopPropagation()}
      >
        <Calendar
          style={{ width: 360 }}
          selectedDate={date ? formatISODate(utc(date)) : undefined}
          handleChange={(newDate, _options, event) => {
            event.stopPropagation();
            setDate(newDate);
            setShowCalendar(false);
            const original = {
              ...value.original,
              expiryDate: formatISODate(utc(newDate))
            } as OptionOriginal;
            onDateChange({ ...value, original }, event);
          }}
          onKeyDown={handleCalendarKeyDown}
        />
      </div>
    </FocusTrap>
  );

  const handleDelete = isLocked ? undefined : (event) => onRemove(value, event);

  const leftContent = (
    <Tooltip
      content={displayValue}
      triggerContainerProps={{
        className: css.displayInBadge,
        style: { maxWidth: badgeRef?.current?.clientWidth }
      }}
    >
      {displayValue}
    </Tooltip>
  );

  const expirationDate = formatDate(utc(date));

  const dateButtonProps: Partial<ButtonProps> = date
    ? {
        text: `${t("time.inWords.expiresShort")} ${expirationDate}`,
        title: t("components.calendarBadge.changeExpirationDate", {
          date: expirationDate,
          label: displayValue
        })
      }
    : {
        icon: "calendar",
        iconPlacement: "right",
        text: t("time.inWords.expires"),
        title: t("components.calendarBadge.setExpirationDate", { label: displayValue })
      };

  const rightContent = (
    <div className={css.date}>
      <Button
        small={small}
        ref={refs.setReference}
        aria-expanded={showCalendar}
        aria-haspopup="dialog"
        kind="blank"
        disabled={isLocked}
        onClick={handleShowCalendar}
        onKeyDown={(event) => {
          event.stopPropagation();
        }}
        tabIndex={tabIndex}
        className={css.calendarButton}
        {...dateButtonProps}
      />
      {date && !isLocked && (
        <ActionIcon
          size={small ? "xxs" : "sm"}
          name="cross"
          title={t("components.calendarBadge.removeExpirationDate", {
            date: expirationDate,
            label: displayValue
          })}
          onClick={(event) => {
            event.stopPropagation();
            setDate(null);
            const original = { ...value.original, expiryDate: null } as OptionOriginal;
            onDateChange({ ...value, original }, event);
            (refs.reference as RefObject<HTMLButtonElement>).current?.focus();
          }}
          onKeyDown={(event) => {
            event.stopPropagation();
          }}
          tabIndex={tabIndex}
        />
      )}
      {showCalendar &&
        ReactDOM.createPortal(CalendarRenderer, document.getElementById("modal_region") as HTMLElement)}
    </div>
  );

  return (
    <div className={css.root} ref={badgeRef}>
      <DoubleSectionPill
        leftContent={leftContent}
        onDelete={handleDelete}
        onDeleteKeyDown={(event) => {
          event.stopPropagation();
        }}
        rightContent={rightContent}
        rightContentClassName={css.rightContentRoot}
        deleteTitle={t("components.calendarBadge.removeOption", { label: displayValue })}
        small={small}
      />
    </div>
  );
};
